import React from "react";
import { Component, type TableColumn } from "@/types";
import {
  TableRowDataEditor,
} from "./TableRowDataEditor";
import { createTableRowEditorFields } from "./table-row-editor-fields";
import {
  canMutateLocalTableRows,
  commitLocalTableRows,
} from "./table-local-row-mutations";

interface DashboardTableProps {
  component: Component;
  mobileViewport?: boolean;
}

type TableRowData = Record<string, unknown> & {
  id?: string;
};

type RowEditorState = {
  mode: "edit";
  rowIndex: number;
  rowData: TableRowData;
};

type TableStyle = React.CSSProperties & {
  "--tm-row-hover"?: string;
};

const FORBIDDEN_STYLE_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getStringProperty = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const getStringOrNumberProperty = (
  value: unknown,
): string | number | undefined =>
  typeof value === "string" || typeof value === "number" ? value : undefined;

const extractRenderableTableStyle = (
  bag: Record<string, unknown> | undefined,
): React.CSSProperties => {
  if (!bag) return {};

  const result: React.CSSProperties = {};
  Object.entries(bag).forEach(([key, value]) => {
    if (FORBIDDEN_STYLE_KEYS.has(key)) return;
    if (typeof value === "string" || typeof value === "number") {
      Object.assign(result, { [key]: value });
    }
  });
  return result;
};

const normalizeTableColumns = (value: unknown): TableColumn[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((column): TableColumn[] => {
    if (!isRecord(column) || typeof column.accessorKey !== "string") {
      return [];
    }

    const id = typeof column.id === "string" && column.id.length > 0
      ? column.id
      : column.accessorKey;
    const header = typeof column.header === "string" && column.header.length > 0
      ? column.header
      : column.accessorKey;

    return [{
      id,
      accessorKey: column.accessorKey,
      header,
      meta: isRecord(column.meta) ? column.meta : undefined,
      size: typeof column.size === "number" ? column.size : undefined,
      minSize: typeof column.minSize === "number" ? column.minSize : undefined,
      maxSize: typeof column.maxSize === "number" ? column.maxSize : undefined,
      enableSorting: typeof column.enableSorting === "boolean" ? column.enableSorting : undefined,
      enableColumnFilter: typeof column.enableColumnFilter === "boolean" ? column.enableColumnFilter : undefined,
      enableResizing: typeof column.enableResizing === "boolean" ? column.enableResizing : undefined,
    }];
  });
};

const normalizeTableRows = (value: unknown): TableRowData[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

function getDashboardRowIndex(target: EventTarget | null): number | null {
  if (!(target instanceof HTMLElement)) return null;

  const rowElement = target.closest("[data-dashboard-table-row-index]");
  if (!(rowElement instanceof HTMLElement)) return null;

  const rowIndex = Number(rowElement.dataset.dashboardTableRowIndex);
  return Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null;
}

export const DashboardTable: React.FC<DashboardTableProps> = ({
  component,
  mobileViewport = false,
}) => {
  const { properties, style } = component;
  const styleRecord = isRecord(style) ? style : undefined;

  // Strip width (handled by ComponentWrapper) AND typography (table has its own Tailwind type system)
  const {
    width: _w, minWidth: _mw, maxWidth: _xw,
    fontSize: _fs, fontWeight: _fw, fontFamily: _ff, fontStyle: _fst,
    color: _clr, letterSpacing: _ls, lineHeight: _lh,
    textTransform: _tt, textDecoration: _td, textAlign: _ta,
    ...visualStyle
  } = styleRecord ?? {};
  const containerStyle = extractRenderableTableStyle(visualStyle);

  // Extract data and columns from properties
  const sourceData = React.useMemo(
    () => normalizeTableRows(properties.data),
    [properties.data],
  );
  const columns = React.useMemo(
    () => normalizeTableColumns(properties.columns),
    [properties.columns],
  );
  const canEditLocalRows = canMutateLocalTableRows(component);
  const canOpenRowEditor = columns.length > 0;
  const [localRowsOverride, setLocalRowsOverride] = React.useState<TableRowData[] | null>(null);
  const [rowEditorState, setRowEditorState] = React.useState<RowEditorState | null>(null);
  const data = canEditLocalRows && localRowsOverride !== null
    ? localRowsOverride
    : sourceData;

  React.useEffect(() => {
    setLocalRowsOverride(null);
    setRowEditorState(null);
  }, [component.id, properties.data]);

  const rowEditorFields = React.useMemo(
    () =>
      createTableRowEditorFields(
        columns,
        data,
        rowEditorState?.rowData ?? {},
      ),
    [columns, data, rowEditorState],
  );

  const openEditRowEditor = React.useCallback(
    (rowIndex: number, rowData: TableRowData) => {
      if (import.meta.env.DEV) {
        console.debug("[DashboardTable] open row editor requested", {
          componentId: component.id,
          rowIndex,
          canOpenRowEditor,
          columnCount: columns.length,
          rowKeys: Object.keys(rowData),
        });
      }

      if (!canOpenRowEditor) return;

      setRowEditorState({
        mode: "edit",
        rowIndex,
        rowData,
      });
    },
    [canOpenRowEditor, columns.length, component.id],
  );

  const handleRowsClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const rowIndex = getDashboardRowIndex(event.target);
      if (rowIndex === null) return;

      const row = data[rowIndex];
      if (!row) return;

      if (import.meta.env.DEV) {
        const target = event.target instanceof HTMLElement ? event.target : null;
        console.debug("[DashboardTable] row click capture", {
          componentId: component.id,
          targetTag: target?.tagName,
          rowIndex,
          canOpenRowEditor,
          columnCount: columns.length,
        });
      }

      event.stopPropagation();
      openEditRowEditor(rowIndex, row);
    },
    [canOpenRowEditor, columns.length, component.id, data, openEditRowEditor],
  );

  const commitRows = React.useCallback(
    (nextRows: TableRowData[]) => {
      if (!canEditLocalRows) return;

      setLocalRowsOverride(nextRows);
      commitLocalTableRows(component, nextRows);
    },
    [canEditLocalRows, component],
  );

  const saveRowEditor = React.useCallback(
    (nextRow: Record<string, unknown>) => {
      if (!rowEditorState || !canEditLocalRows) return;

      const nextRows = data.map((row, index) =>
        index === rowEditorState.rowIndex
          ? { ...row, ...nextRow }
          : row,
      );
      commitRows(nextRows);
      setRowEditorState(null);
    },
    [canEditLocalRows, commitRows, data, rowEditorState],
  );

  const deleteEditedRow = React.useCallback(() => {
    if (!rowEditorState || !canEditLocalRows) return;

    commitRows(data.filter((_, index) => index !== rowEditorState.rowIndex));
    setRowEditorState(null);
  }, [canEditLocalRows, commitRows, data, rowEditorState]);

  // Extract dashboard-specific properties with defaults
  const rowSpacing = getStringProperty(properties.rowSpacing) ?? "tight";
  const rowPadding = getStringProperty(properties.rowPadding) ?? "compact";
  const headerStyle = getStringProperty(properties.headerStyle) ?? "typography";
  const columnWidthStrategy = getStringProperty(properties.columnWidthStrategy) ?? "fixed";
  const showBorders = properties.showBorders !== false;
  const headerBgColor = typeof properties.headerBackgroundColor === 'string' ? properties.headerBackgroundColor : undefined;
  const headerTxtColor = typeof properties.headerTextColor === 'string' ? properties.headerTextColor : undefined;
  const striped = properties.striped !== false; // Default true to preserve existing always-striped behavior
  const hoverable = properties.hoverable === true;
  const fontSize = getStringOrNumberProperty(properties.fontSize);
  const bodyTextColor = typeof properties.bodyTextColor === 'string' ? properties.bodyTextColor : undefined;
  const alternateRowColor = typeof properties.alternateRowColor === 'string' ? properties.alternateRowColor : undefined;
  const rowHoverColor = typeof properties.rowHoverColor === 'string' ? properties.rowHoverColor : undefined;
  const tableBorderColor = typeof properties.borderColor === 'string' ? properties.borderColor : undefined;

  // Convert spacing values to CSS classes
  const getRowSpacingClass = (spacing: string) => {
    switch (spacing) {
      case "none": return "space-y-0";
      case "tight": return "space-y-0";
      case "normal": return "space-y-1";
      case "loose": return "space-y-2";
      default: return "space-y-0";
    }
  };

  const getRowPaddingClass = (padding: string) => {
    switch (padding) {
      case "compact": return "py-1.5";
      case "normal": return "py-2.5";
      case "spacious": return "py-3.5";
      default: return "py-1.5";
    }
  };

  const getColumnWidth = (index: number) => {
    switch (columnWidthStrategy) {
      case "auto":
        return { flex: 1 };
      case "content":
        return {
          width: index === 0 ? 'auto' : 'auto',
          minWidth: index === 0 ? '120px' : '80px',
          flex: 'none'
        };
      case "fixed":
      default:
        return {
          width: index === 0 ? '180px' : '100px',
          flexShrink: 0
        };
    }
  };

  const hoverContainerStyle: TableStyle = rowHoverColor
    ? { "--tm-row-hover": rowHoverColor }
    : {};

  return (
    <div
      className="w-full h-full"
      style={containerStyle}
      data-table-renderer-kind="dashboard"
    >
      {/* Title and Description */}
      {Boolean(properties.title) && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            {String(properties.title || '')}
          </h3>
          {Boolean(properties.description) && (
            <p className="text-sm text-muted-foreground leading-tight">
              {String(properties.description || '')}
            </p>
          )}
        </div>
      )}

      {/* Clean Dashboard Table */}
      <div
        className="space-y-2 w-full overflow-hidden"
        style={hoverContainerStyle}
      >
        {/* Configurable Header Row */}
        <div
          className={`flex items-center px-2 py-2 ${
            headerBgColor ? 'rounded-lg' : (
              headerStyle === 'background'
                ? 'bg-muted/50 rounded-lg'
                : showBorders ? 'border-b border-border/60' : ''
            )
          }`}
          style={{
            ...(headerBgColor ? { backgroundColor: headerBgColor } : {}),
            ...(tableBorderColor && showBorders && headerStyle !== 'background' ? { borderBottomColor: tableBorderColor } : {}),
          }}
        >
          {columns.map((column, index) => (
            <div
              key={column.id}
              className={`${fontSize ? '' : 'text-xs'} font-bold ${
                headerTxtColor ? '' : (
                  headerStyle === 'background'
                    ? 'text-foreground/80'
                    : 'text-muted-foreground uppercase tracking-wide'
                )
              } ${index === 0 ? 'text-left' : 'text-center'} truncate`}
              style={{
                ...getColumnWidth(index),
                ...(headerTxtColor ? { color: headerTxtColor } : {}),
                ...(fontSize ? { fontSize: typeof fontSize === 'number' ? `${fontSize}px` : fontSize } : {}),
              }}
            >
              {String(column.header || '')}
            </div>
          ))}
        </div>

        {/* Configurable Data Rows */}
        <div
          className={`${getRowSpacingClass(rowSpacing)} mt-1`}
          onClickCapture={handleRowsClickCapture}
        >
          {data.map((row, rowIndex) => (
            <div
              key={rowIndex}
              data-dashboard-table-row-index={rowIndex}
              className={`flex items-center px-2 ${getRowPaddingClass(rowPadding)} transition-colors ${
                striped && rowIndex % 2 === 1 && !alternateRowColor ? 'bg-muted/25' : ''
              } ${hoverable ? (rowHoverColor ? 'hover:bg-[var(--tm-row-hover)]' : 'hover:bg-muted/50') : ''
              } ${showBorders && rowIndex < data.length - 1 ? (tableBorderColor ? 'border-b' : 'border-b border-border/50') : ''} ${
                canOpenRowEditor ? 'cursor-pointer' : ''
              }`}
              style={{
                ...(striped && rowIndex % 2 === 1 && alternateRowColor ? { backgroundColor: alternateRowColor } : {}),
                ...(tableBorderColor && showBorders && rowIndex < data.length - 1 ? { borderBottomColor: tableBorderColor } : {}),
              }}
              tabIndex={canOpenRowEditor ? 0 : undefined}
              onClick={(event) => {
                event.stopPropagation();
                openEditRowEditor(rowIndex, row);
              }}
              onKeyDown={(event) => {
                if (!canOpenRowEditor) return;
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                openEditRowEditor(rowIndex, row);
              }}
            >
              {columns.map((column, colIndex) => (
                <div
                  key={column.id}
                  className={`${fontSize ? '' : 'text-sm'} ${
                    colIndex === 0
                      ? `font-medium ${bodyTextColor ? '' : 'text-foreground'} text-left`
                      : `text-center ${bodyTextColor ? '' : 'text-foreground/80'} font-semibold`
                  } truncate`}
                  style={{
                    ...getColumnWidth(colIndex),
                    ...(bodyTextColor ? { color: bodyTextColor } : {}),
                    ...(fontSize ? { fontSize: typeof fontSize === 'number' ? `${fontSize}px` : fontSize } : {}),
                  }}
                >
                  {String(row[column.accessorKey] || '-')}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <TableRowDataEditor
        open={rowEditorState !== null}
        mode="edit"
        fields={rowEditorFields}
        rowData={rowEditorState?.rowData ?? {}}
        mobileViewport={mobileViewport}
        readOnly={!canEditLocalRows}
        onOpenChange={(open) => {
          if (!open) setRowEditorState(null);
        }}
        onSave={saveRowEditor}
        onDelete={canEditLocalRows ? deleteEditedRow : undefined}
      />
    </div>
  );
};
